"use client";

import type { DeepPartial } from "@apollo/client/utilities";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { LuExternalLink } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useOwnTheme } from "@/contexts/theme-context";
import type { CoreAccountFieldsFragment } from "@/graphql/codegen/graphql";
import { DEFAULT_PROFILE_PIC } from "@/utils/constants";
import { getUserProfilePicture, hasNFTProfilePic } from "@/utils/helpers";
import { cn } from "@/utils/lib";

interface DesoAvatarProps {
  account: Pick<
    DeepPartial<CoreAccountFieldsFragment>,
    "publicKey" | "username" | "extraData"
  > | null;
  className?: string;
  size?: number;
  borderSize?: number;
  clickable?: boolean;
  imgURL?: string;
  fallbackClassName?: string;
}

const getInitialsFontSize = (size: number): string => {
  if (size <= 26) return "8px";
  if (size <= 34) return "12px";
  if (size <= 42) return "16px";
  return "20px";
};

const getInitials = (name: string): string => {
  const trimmedName = name.trim();

  if (trimmedName === "") {
    return "";
  }

  // Trim and split the name by spaces (treating _ as space also)
  const words = trimmedName.split(/[\s_]+/);

  // If the name contains spaces, follow the existing logic
  if (words.length > 1) {
    const firstInitial = words[0][0]?.toUpperCase() ?? "";
    const lastInitial = words[1][0]?.toUpperCase() ?? "";
    return firstInitial + lastInitial;
  }

  // If all is uppercase, return the first character only
  if (trimmedName === trimmedName.toUpperCase()) {
    return trimmedName[0];
  }

  // If the name does not contain spaces, check for camel case
  const camelCaseInitials = trimmedName.match(/[A-Z]/g);
  if (camelCaseInitials && camelCaseInitials.length > 0) {
    // Max 2 characters
    return camelCaseInitials.join("").slice(0, 2);
  }

  // If everything else fails to check, let's just return the first character
  // as a safety net
  return words[0][0].toUpperCase();
};

const darkMaterialColors = [
  "#D32F2F", // Red darken-2
  "#C2185B", // Pink darken-2
  "#7B1FA2", // Purple darken-2
  "#512DA8", // Deep Purple darken-2
  "#303F9F", // Indigo darken-2
  "#1976D2", // Blue darken-2
  "#0288D1", // Light Blue darken-2
  "#0097A7", // Cyan darken-2
  "#00796B", // Teal darken-2
  "#388E3C", // Green darken-2
  "#689F38", // Light Green darken-2
  "#AFB42B", // Lime darken-2
  "#FBC02D", // Yellow darken-2
  "#FFA000", // Amber darken-2
  "#F57C00", // Orange darken-2
  "#E64A19", // Deep Orange darken-2
  "#5D4037", // Brown darken-2
  "#616161", // Grey darken-2
  "#455A64", // Blue Grey darken-2
];

const lightMaterialColors = [
  "#FFCDD2", // Red lighten-4
  "#F8BBD0", // Pink lighten-4
  "#E1BEE7", // Purple lighten-4
  "#D1C4E9", // Deep Purple lighten-4
  "#C5CAE9", // Indigo lighten-4
  "#BBDEFB", // Blue lighten-4
  "#B3E5FC", // Light Blue lighten-4
  "#B2EBF2", // Cyan lighten-4
  "#B2DFDB", // Teal lighten-4
  "#C8E6C9", // Green lighten-4
  "#DCEDC8", // Light Green lighten-4
  "#F0F4C3", // Lime lighten-4
  "#FFF9C4", // Yellow lighten-4
  "#FFECB3", // Amber lighten-4
  "#FFE0B2", // Orange lighten-4
  "#FFCCBC", // Deep Orange lighten-4
  "#D7CCC8", // Brown lighten-4
  "#F5F5F5", // Grey lighten-4
  "#CFD8DC", // Blue Grey lighten-4
];

const PROFILE_AVATAR_FALLBACKS = {
  [process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY]: "/assets/coin-deso.png",
};

enum ImageStatus {
  Loaded,
  Error,
}

const ImageStatusCache = new Map<string, ImageStatus>();

const getAvatarContent = (
  account: Pick<
    DeepPartial<CoreAccountFieldsFragment>,
    "publicKey" | "username" | "extraData"
  > | null,
  size: number,
) => {
  if (account?.publicKey && PROFILE_AVATAR_FALLBACKS[account.publicKey]) {
    return (
      <Image
        src={PROFILE_AVATAR_FALLBACKS[account.publicKey]}
        alt="profile-fallback"
        width={size}
        height={size}
        quality={90}
        decoding="async"
      />
    );
  }

  if (account?.username) {
    return (
      <span style={{ fontSize: getInitialsFontSize(size) }}>
        {getInitials(account.username)}
      </span>
    );
  }

  return (
    <Image
      src={DEFAULT_PROFILE_PIC}
      alt="default-pic"
      width={size}
      height={size}
      quality={90}
      decoding="async"
    />
  );
};

const DesoAvatar = ({
  account,
  className,
  size = 18,
  borderSize = 1,
  clickable = true,
  imgURL = "",
  fallbackClassName,
}: DesoAvatarProps) => {
  const profilePic = imgURL || getUserProfilePicture(account);
  const imageStatus = ImageStatusCache.get(profilePic);
  const [, forceImageStatusRefresh] = useState(0);
  const error = imageStatus === ImageStatus.Error;
  const loaded = imageStatus === ImageStatus.Loaded;
  const { darkTheme } = useOwnTheme();

  const stringToColor = useCallback(
    (str: string) => {
      // Generate a hash from the input string
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      hash = Math.abs(hash);

      const colorsList = darkTheme ? darkMaterialColors : lightMaterialColors;
      const colorIndex = hash % colorsList.length;

      return colorsList[colorIndex];
    },
    [darkTheme],
  );

  const updateImageStatus = useCallback(
    (status: ImageStatus) => {
      ImageStatusCache.set(profilePic, status);
      forceImageStatusRefresh((version) => version + 1);
    },
    [profilePic],
  );

  const handleError = useCallback(() => {
    updateImageStatus(ImageStatus.Error);
  }, [updateImageStatus]);

  const handleLoad = useCallback(() => {
    updateImageStatus(ImageStatus.Loaded);
  }, [updateImageStatus]);

  const avatarContent = useMemo(
    () => getAvatarContent(account, size),
    [account, size],
  );

  const avatar = (
    <div
      className={
        (cn("opacity-100 transition-opacity delay-100 duration-700 ease-in", {
          // Both error or loaded means it's finished loading
          "opacity-0": loaded || error,
        }),
        fallbackClassName)
      }
    >
      {hasNFTProfilePic(account?.extraData) && !imgURL ? (
        <InfoTooltip
          mobile={false}
          text={
            <Link
              href={`https://nftz.me/nft/${account?.extraData?.NFTProfilePicturePostHashHex}`}
              target="_blank"
              className="items-center gap-1 text-muted"
              onClick={(e) => e.stopPropagation()}
            >
              View NFT
              <LuExternalLink />
            </Link>
          }
        >
          <div
            className={cn(
              `
                deso-avatar-nft nft__avatar relative mx-auto my-0
                bg-foreground
              `,
              className,
              "border-0",
              clickable && "cursor-pointer",
            )}
            style={{
              width: size,
              height: size,
              marginTop: borderSize ? borderSize * -1 : "inherit",
            }}
          >
            <div
              className="deso-avatar-nft-frame nft__hex-background absolute"
              style={{
                width: size - borderSize * 2,
                height: size - borderSize * 2,
                top: borderSize,
                left: borderSize,
              }}
            >
              <Image
                className="
                  deso-avatar-nft-image absolute min-h-full min-w-full
                  object-cover
                "
                width={size}
                height={size}
                src={profilePic}
                alt="avatar"
                quality={90}
                onError={handleError}
                onLoad={handleLoad}
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </InfoTooltip>
      ) : (
        <Avatar
          className={cn(
            "size-5 border bg-background",
            className,
            clickable && "cursor-pointer",
          )}
          style={{
            width: size,
            height: size,
            borderWidth: borderSize,
          }}
        >
          {(imgURL || account) && !error ? (
            <Image
              src={profilePic}
              alt="avatar"
              className={cn("min-h-full min-w-full object-cover opacity-0", {
                "opacity-100": loaded && !error,
              })}
              width={size}
              height={size}
              quality={90}
              onError={handleError}
              onLoad={handleLoad}
              loading="eager"
              decoding="async"
            />
          ) : (
            <AvatarFallback
              className="opacity-100"
              style={{
                background: stringToColor(account?.publicKey || ""),
                color: "#000",
              }}
            >
              {avatarContent}
            </AvatarFallback>
          )}
        </Avatar>
      )}
    </div>
  );

  if (!clickable || !account) {
    return avatar;
  }

  return (
    <Link
      href={`/${account.username || account.publicKey}`}
      onClick={(e) => e.stopPropagation()}
      prefetch={false}
    >
      {avatar}
    </Link>
  );
};

export default DesoAvatar;
